import * as React from "react";
import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Link } from "react-router-dom";
import ShiftTimeLine from "./ShiftBoxTimeline";
import { Avatar, Chip, TextField, Typography, useTheme } from "@mui/material";
import './Shifts.css';

export default function ShiftBox(props) {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const [startvalue, setStartValue] = useState(new Date(props.shift.startDate));
    const [endvalue, setEndValue] = useState(
        !props.shift.endDate ? new Date() : new Date(props.shift.endDate)
    );
    const [breakTime, setBrakeTime] = useState(
        Array.isArray(props.shift.break) ? props.shift.break : []
    );
    const [shiftLength, setShiftLength] = useState(props.shift.totalShiftLength);
    const [totalBreak, setTotalBreak] = useState(props.shift.totalBreak);
    const [shiftWOBreak, setShiftWOBreak] = useState(props.shift.shiftWithoutBreak);
    
    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    // Helper function to format time as HH:MM
    function formatTimeHHMM(timeString) {
        if (!timeString) return "00:00";

        // If it's a Date object
        if (timeString instanceof Date) {
            const hours = timeString.getHours().toString().padStart(2, '0');
            const minutes = timeString.getMinutes().toString().padStart(2, '0');
            return `${hours}:${minutes}`;
        }
        if (typeof timeString === 'string' && timeString.includes(':')) {
            return timeString.substring(0, 5);
        }
        return "00:00";
    }

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
    };

    function convertS3ToCloudFront(url) {
        // Replace the AWS S3 domain with the CloudFront domain
        if (!url) return "";
        const cloudFrontDomain = 'https://d2k8ghk99lpcqm.cloudfront.net/';
        const s3Domain = 'https://cms-shifts.s3.ap-southeast-2.amazonaws.com/';
        
        // Check if the URL starts with the S3 domain
        if (url && url.startsWith(s3Domain)) {
            // Replace the S3 domain with the CloudFront domain
            const convertedUrl = url.replace(s3Domain, cloudFrontDomain);
            return convertedUrl;
        } else {
            // URL doesn't match the expected pattern
            return url;
        }
    }

    const subtractTime = (time1, time2) => {
        const date1 = new Date(time1);
        const date2 = new Date(time2);
        const diff = date2.getTime() - date1.getTime();
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    };

    // Calculate time difference in HH:MM format ignoring seconds
    function calculateTimeDifferenceHHMM(startTime, endTime) {
        let start = new Date(startTime);
        let end = new Date(endTime || new Date());

        // Set seconds and milliseconds to zero to ignore them
        start.setSeconds(0, 0);
        end.setSeconds(0, 0);

        let diff = Math.abs(end - start);

        let hours = Math.floor(diff / 3.6e6);
        let minutes = Math.floor((diff % 3.6e6) / 6e4);

        // Format as HH:MM
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    function calculateTotalBreak() {
        let totalMinutes = 0;

        breakTime.forEach((brk) => {
            let start = new Date(brk.start);
            let end = new Date(brk.end || new Date());

            // Set seconds to 0 to ignore them
            start.setSeconds(0, 0);
            end.setSeconds(0, 0);

            let diff = Math.abs(end - start);
            totalMinutes += Math.floor(diff / 60000); // Convert ms to minutes
        });

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        const total = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
        setTotalBreak(total);
    }

    function calculateTotalShiftLength() {
        let start = new Date(startvalue);
        let end = new Date(endvalue);

        // Set seconds to 0 to ignore them
        start.setSeconds(0, 0);
        end.setSeconds(0, 0);

        let diff = Math.abs(end - start);

        let hours = Math.floor(diff / 3.6e6);
        let minutes = Math.floor((diff % 3.6e6) / 6e4);

        const total = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
        setShiftLength(total);
    }

    const calculateshiftWithoutBreak = () => {
        // Parse times from HH:MM:SS format
        const [breakHours, breakMinutes] = totalBreak.split(':').map(Number);
        const [shiftHours, shiftMinutes] = shiftLength.split(':').map(Number);

        // Calculate total minutes
        const totalBreakMinutes = breakHours * 60 + breakMinutes;
        const totalShiftMinutes = shiftHours * 60 + shiftMinutes;

        // Calculate shift without break in minutes
        let remainingMinutes = totalShiftMinutes - totalBreakMinutes;

        // Handle negative case (if break > shift)
        if (remainingMinutes < 0) remainingMinutes = 0;

        // Convert back to hours and minutes
        const hours = Math.floor(remainingMinutes / 60);
        const minutes = remainingMinutes % 60;

        // Format result
        const total = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
        setShiftWOBreak(total);
    };

    useEffect(() => {
        calculateTotalBreak();
        calculateTotalShiftLength();
        calculateshiftWithoutBreak();
    }, [
        startvalue,
        endvalue,
        breakTime,
        totalBreak,
        shiftLength,
        shiftWOBreak,
    ]);

    const {
        startImage,
        endImage,
        startDate,
        endDate,
        approved
    } = props.shift;

    return (
        <>
        <Box
            sx={{
                width: "120px",
                borderRadius: "20px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                textAlign: "center",
                cursor: "pointer",  // Add a pointer cursor when hovering
                backgroundColor: theme.palette.card.bg,
                transition: "background-color 0.3s ease", // Add a smooth transition for background color change
                margin: "auto",
                marginTop: "5px",
            }}
            onClick={handleClickOpen}
        >
            <Box
                sx={{
                    fontWeight: "500",
                    backgroundColor: approved ? theme.palette.success.main : theme.palette.info.main,
                    color:theme.palette.text.secondary,
                    borderRadius: "7px",
                    width: "80%",
                    margin: "10px 2px 2px 2px", // Center the box with auto margin
                    textAlign: "center",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Box sx={{ padding: "0" }}>
                {formatTime(startDate).slice(0, 5)} -{" "}
                {endDate ? (
                    formatTime(endDate).slice(0, 5)
                ) : (
                    <span>Active</span>
                )}
                </Box>
            </Box>
            <Box>
                <Box>
                    Shift:{" "}
                    <span>{formatTimeHHMM(shiftLength)}</span>
                </Box>
                <Box>
                    Break: <span>{formatTimeHHMM(totalBreak)}</span>
                </Box>
            </Box>
            <Box>
                Shift w/o break:{" "}
                <span>{formatTimeHHMM(shiftWOBreak)} </span>
            </Box>
        </Box>

            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                    style: {
                        backgroundColor: theme.palette.dialog.paper,
                        borderRadius: "20px",
                        color: theme.palette.text.secondary,
                        width: "max-content",
                        margin: "auto"
                    },
                }}
            >
                <DialogContent style={{ display: "flex", padding: "0", color: theme.palette.text.primary }}>
                    <div
                        style={{
                            padding: "20px",
                            backgroundColor: theme.palette.card.bg,
                            width: "max-content",
                            color: theme.palette.text.primary
                        }}
                    >
                        <Avatar
                            sx={{ width: 100, height: 100, margin: "auto", marginBottom: "0.6rem", fontSize: "3rem",color: theme.palette.text.secondary }}
                        >
                            {props.employee.fname[0].toUpperCase()}
                        </Avatar>

                        <Link style={{ textDecoration: "none", color: theme.palette.text.primary}} to={`/employee/${props.employeeId}`}>
                            <div
                                style={{
                                    fontSize: "1.1rem",
                                    fontWeight: "600",
                                    textAlign: "center",
                                }}
                            >
                                {props.employee.fname} {props.employee.lname}
                            </div>
                        </Link>
                        <p style={{ textAlign: "center", marginTop: "0", marginBottom: "0.2rem" }}>
                            {props.employee.title}
                        </p>
                        <Grid container spacing={2}>
                            <Grid item xs={3} md={3}>
                                <AccessTimeIcon />
                            </Grid>
                            <Grid item xs={9} md={9}>
                                <span style={{ fontWeight: "600", textAlign: "center" }}>
                                    {formatTimeHHMM(shiftLength)}
                                </span>
                                <br />
                                <span style={{ fontSize: "0.8rem", textAlign: "center" }}>Total length</span>
                                <br />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={3} md={3}></Grid>
                            <Grid item xs={9} md={9}>
                                <span style={{ fontWeight: "600", textAlign: "center" }}>{formatTimeHHMM(totalBreak)}</span>
                                <br />
                                <span style={{ fontSize: "0.8rem", textAlign: "center" }}>Total Break</span>
                                <br />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={3} md={3}></Grid>
                            <Grid item xs={9} md={9}>
                                <span
                                    style={{
                                        fontWeight: "600",
                                        textAlign: "center",
                                        color: "#ee5622",
                                    }}
                                >
                                    {formatTimeHHMM(shiftWOBreak)}
                                </span>
                                <br />
                                <span style={{ fontSize: "0.8rem", textAlign: "center" }}>Shift w/o break</span>
                                <br />
                            </Grid>
                        </Grid>
                    </div>
                    <div style={{ padding: "20px" }}>

                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <h4 style={{ fontWeight: "400" }}>
                                Edit Shift on:{" "}
                                <span style={{ fontWeight: "600" }}>
                                    {new Date(props.shift.startDate).toString().slice(0, 15)}
                                </span>
                            </h4>
                            {!props.shift.endDate && <h4 style={{ color: theme.palette.warning.main }}>Currently Active</h4>}
                        </div>

                        <Grid container spacing={2}>
                            <Grid item xs={5}>
                                <Typography variant="body1" style={{ fontWeight: "600" }}>
                                    Start Time:  <Chip
                                        label={formatTime(props.shift.startDate).slice(0, 5)}
                                        variant="outlined"
                                        sx={{color: theme.palette.text.primary}}
                                        size="small"
                                        style={{ marginRight: "5px" }}
                                    />
                                </Typography>
                            </Grid>
                            <Grid item xs={5}>
                                <Typography variant="body1" style={{ fontWeight: "600" }}>
                                    End Time:  <Chip
                                        label={formatTime(props.shift.endDate).slice(0, 5)}
                                        variant="outlined"
                                        sx={{color: theme.palette.text.primary}}
                                        size="small"
                                        style={{ marginRight: "5px" }}
                                    />
                                </Typography>
                            </Grid>
                            <Grid item xs={5}>
                                <img
                                    src={startImage && convertS3ToCloudFront(startImage)}
                                    alt="Start Shift Image"
                                    style={{
                                        width: "100%",
                                        marginTop: "8px",
                                        borderRadius: "5px",
                                    }}
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <img
                                    src={endImage && convertS3ToCloudFront(endImage)}
                                    alt="End Shift Image"
                                    style={{
                                        width: "100%",
                                        marginTop: "8px",
                                        borderRadius: "5px",
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Typography variant="h6" style={{ fontWeight: "600", marginTop: "10px" }}>
                            Breaks
                        </Typography>
                        {props.shift.break === null 
                        ||
                        props.shift.break.length === 0
                        
                        ? (
                            <div>No breaks Found.</div>
                        ) : (
                            props.shift.break.map((brk, index) => (
                                <Grid container spacing={2} key={brk.start}>
                                    <Grid item xs={10}>
                                        <Typography variant="body1" >
                                            Break {index + 1}:  <Chip
                                                label={formatTime(brk.start).slice(0, 5) + " to " + formatTime(brk.end).slice(0, 5) + " = " + subtractTime(brk.start, brk.end)}
                                                variant="outlined"
                                                sx={{color: theme.palette.text.primary}}
                                                size="small"
                                                style={{ marginRight: "5px" }}
                                            />
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ))
                        )}
                        <ShiftTimeLine shiftTimelines={props.shift.shiftTimelines} />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
